import { useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { DragDropContainer, DraggablePhotos, Modal } from '../../shared';

import { useMediaProcessingStatus } from '../../hooks/medias';

import { assert } from '../../utils/assert';
import {
    onSelectVideo,
    OnVideoUploadComplete,
    VideoFileUploading,
} from '../../utils/upload';

import { MediaType } from '../../types/enums/mediatype.enum';

import { Media } from '../../types/mediaTypes';

import dogImg from '../../assets/dog.png';

type PackVideosSectionProps = {
    items: (Media | (VideoFileUploading & { id?: number }))[];
    onVideoSelected: (item: VideoFileUploading) => void;
    onVideoWasUploaded: (file: File, media: Media) => void;
    onVideoDelete: (index: number, type: MediaType) => void;
    onVideoDragEnd: (result: {
        source: { index: number; droppableId: MediaType };
        destination: { index: number };
    }) => void;
};

export const PackVideosSection = (props: PackVideosSectionProps) => {
    const {
        items,
        onVideoSelected,
        onVideoWasUploaded: onVideoWasUploadedOriginal,
        onVideoDelete,
        onVideoDragEnd,
    } = props;

    const videoFileUploadingRef = useRef<VideoFileUploading | null>(null);

    const {
        media: processingMedia,
        processingStatus,
        trackMediaProcessing,
    } = useMediaProcessingStatus({
        onProcessingComplete: (media) => {
            assert(videoFileUploadingRef.current, 'Video uploading is falsey');

            onVideoWasUploadedOriginal(
                videoFileUploadingRef.current.file,
                media,
            );
            videoFileUploadingRef.current = null;
        },
        onProcessingError: () => {
            Modal.alert({
                heading: 'Processing Failed',
                text: 'The server failed to process the given file',
            });
            videoFileUploadingRef.current = null;
        },
    });

    const onVideoWasSelected = (file: VideoFileUploading) => {
        if (videoFileUploadingRef.current) {
            return;
        }

        videoFileUploadingRef.current = file;
        onVideoSelected(file);
    };
    const onVideoWasUploaded: OnVideoUploadComplete = (_file, res) => {
        assert(
            res.data.uploaded.length === 1,
            'Uploaded length is not expected length of 1',
        );
        assert(videoFileUploadingRef.current, 'Video uploading is falsey');

        const media = res.data.uploaded[0];
        onVideoWasUploadedOriginal(videoFileUploadingRef.current.file, media);
        trackMediaProcessing(media);
    };

    const isProcessing = processingStatus?.processingState === 'in_progress';

    return (
        <DndProvider backend={HTML5Backend}>
            <DragDropContainer
                type={MediaType.VIDEO}
                onMediasSelected={onVideoWasSelected}
                onMediasWasUploaded={onVideoWasUploaded}
            >
                <>
                    <h3>Videos</h3>
                    {items.length === 0 && (
                        <div className="noMediaWrapper">
                            <img
                                alt="placeholder no uploaded video"
                                src={dogImg}
                            ></img>
                            <label className="placeholder">
                                No video? Ohno. Drag and drop here to upload :)
                            </label>
                        </div>
                    )}
                    <DraggablePhotos
                        onDelete={onVideoDelete}
                        type={MediaType.VIDEO}
                        items={items.map((item) =>
                            item.id === processingMedia?.id
                                ? {
                                      id: item.url,
                                      url: item.url,
                                      uploading:
                                          ('uploading' in item &&
                                              item.uploading) ||
                                          isProcessing,
                                      uploadingText: isProcessing
                                          ? `Processing ${processingStatus.processingProgress}%`
                                          : 'Uploading',
                                  }
                                : item,
                        )}
                        onDragEnd={onVideoDragEnd}
                    />

                    <div style={{ marginTop: 20 }}>
                        <input
                            type="file"
                            name=""
                            id="videos"
                            accept="video/*"
                            disabled={Boolean(videoFileUploadingRef.current)}
                            onChange={(e) =>
                                onSelectVideo(
                                    e,
                                    onVideoWasSelected,
                                    onVideoWasUploaded,
                                    {
                                        compress: true,
                                    },
                                )
                            }
                        />
                        <label className="upload" htmlFor="videos">
                            Add new
                        </label>
                    </div>
                </>
            </DragDropContainer>
        </DndProvider>
    );
};
